import Container from 'typedi';
import { LoggerProvider } from '../logger';
import DataStoreHelper from '../../../Modules/Utils/DataStoreHelper';
import { IWebLoginInitObject } from '../web-login/web-login.component';
import { CookieStoreItemService } from '../storage/implementations';
import { UrlUtils } from '../utils/urlUtils';
import { PlayerMigratedCookie } from './enums/consts';
import { v4 as uuidv4, validate as isValidUUID } from 'uuid';
import { Utils } from '../utils';
import { IMrGreenMigration } from './models/interfaces/IMrGreenMigration';
import { MrGreenMigrationLogging } from './utils/mr-green-migration.logging.utils';
import { IMrGreenMigrationEventSender } from './models/interfaces/IMrGreenMigrationEventSender';
import { IMigrationTokenItem } from './models/interfaces/IMigrationTokenItem';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import DeferredObject from '../../../Modules/Utils/DeferredObject';
import { INativeService } from '../native/models/INativeService';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ILogger } from '@sparkware/uc-sdk-core';
import { WindowToken } from '../../injection-tokens';

export class MrGreenMigration implements IMrGreenMigration {
    private _eSender: IMrGreenMigrationEventSender;
    private _businessCorrelationId: string;
    private _token: string;
    private _source: string;
    private _playerMigratedCookieExist: boolean;
    private _migrationAutoLoginOcurred: boolean;
    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _cookieStoreItemService: CookieStoreItemService;
    private readonly _urlUtils: UrlUtils;
    private readonly _utils: Utils;
    private readonly _migrationStateId: number;
    private readonly _localStorageItemService: LocalSimpleStoreService;
    private readonly _correlationID: string;
    private readonly _isNative: boolean;
    private readonly _migrationDataReadyDO: DeferredObject<void>;
    private readonly _mrGreenMigrationLogging: MrGreenMigrationLogging;

    public get BusinessCorrelationId(): string {
        return this._businessCorrelationId;
    }

    public get PlayerMigratedCookieExist(): boolean {
        return this._playerMigratedCookieExist;
    }

    public get Token(): string {
        let migrationTokenData: IMigrationTokenItem = JSON.parse(
            this._localStorageItemService.get('mrGreenMigrationToken'),
        );
        if (migrationTokenData && !migrationTokenData.Used && migrationTokenData.Value)
            return migrationTokenData.Value;
        else return null;
    }

    public get Source(): string {
        return this._source;
    }

    public get MigrationAutoLoginOcurred(): boolean {
        return this._migrationAutoLoginOcurred;
    }

    public set MigrationAutoLoginOcurred(value: boolean) {
        this._migrationAutoLoginOcurred = value;
    }

    private get _nativeServicePromise(): Promise<INativeService> {
        return LoaderManager.Instance.NativeServiceLoader.Instance;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('MrGreenMigration');
        this._cookieStoreItemService = Container.get(CookieStoreItemService);
        this._urlUtils = Container.get(UrlUtils);
        this._utils = Container.get(Utils);
        this._localStorageItemService = Container.get(LocalSimpleStoreService);
        this._mrGreenMigrationLogging = Container.get(MrGreenMigrationLogging);
        this._migrationDataReadyDO = new DeferredObject<void>();

        this._migrationStateId =
            this._window.pageContextManager.getMigrationData()?.configuration?.stateId;
        this._isNative = this._utils.findIfIsNative();

        this._setMigrationValues();
        this._correlationID = uuidv4();
        this._playerMigratedCookieExist =
            this._cookieStoreItemService.get(PlayerMigratedCookie.key) != undefined;

        this._migrationAutoLoginOcurred = false;
    }

    public OnLoginSuccess = () => {
        this._logger.debug('MrGreenMigration - Execute logic for login success');
        if (
            this._migrationStateId &&
            this._migrationStateId == 30 &&
            !this._playerMigratedCookieExist
        ) {
            this._cookieStoreItemService.set(PlayerMigratedCookie.key, '', {
                expires: PlayerMigratedCookie.expirationDays,
                secure: true,
                path: '/',
                domain: this._urlUtils.getDomain(),
            });
            this._playerMigratedCookieExist = true;
        }
    };

    public OnLoginFailed = () => {
        this._logger.debug('MrGreenMigration - Execute logic for login failed');
        if (this._playerMigratedCookieExist) {
            this._cookieStoreItemService.remove(PlayerMigratedCookie.key);
            this._playerMigratedCookieExist = false;
        }
    };

    public SetInitWebLoginObjectProperties = (initObject: IWebLoginInitObject) => {
        this._logger.debug('MrGreenMigration - set properties for web login object');

        if (this._migrationStateId) initObject.migrationStateId = this._migrationStateId;

        return initObject;
    };

    public SendBiVisitEvent = async () => {
        if (this._migrationStateId && this._migrationStateId == 30) {
            this._logger.debug('MrGreenMigration - Send BI Event on visit web/app');
            await this._migrationDataReadyDO.promise;
            this._eSender = this._mrGreenMigrationLogging.GetElasticEventSender(
                this._correlationID,
                this._businessCorrelationId,
                this._token,
                this._source,
                this._playerMigratedCookieExist,
            );
            this._eSender.sendBIOnVisitEvent('SendBiVisitEvent');
        }
    };

    public SetMigrationTokenUsed = () => {
        const migrationTokenData: IMigrationTokenItem = {
            Used: true,
            Value: this._token,
        };
        this._localStorageItemService.set(
            'mrGreenMigrationToken',
            JSON.stringify(migrationTokenData),
        );
    };

    public IsValidForAutoLogin = (): boolean => {
        return this.Token && (this._isNative || this._source === 'player_migration');
    };

    private _setMigrationValues = async () => {
        const nativeService = await this._nativeServicePromise;
        if (nativeService)
            nativeService.NativeStoreReadyDO.promise.then(() => {
                this._setMigrationValuesFromNativeStore();
                this._migrationDataReadyDO.resolve();
            });
        else {
            this._setMigrationValuesFromScut();
            this._migrationDataReadyDO.resolve();
        }
    };

    private _setMigrationValuesFromScut = () => {
        const sCut = this._utils.GetSCutObject();

        this._businessCorrelationId = this._getValidateBusinessCorrelationId(sCut?.get('utm_id'));
        this._source = sCut?.get('utm_source');

        this._setMigrationToken(sCut?.get('utm_content'));
    };

    private _setMigrationValuesFromNativeStore = () => {
        const mrGreenMigrationValues = DataStoreHelper.getNativeStoreItem('MrGreenMigrationValues');
        this._businessCorrelationId = this._getValidateBusinessCorrelationId(
            mrGreenMigrationValues?.BusinessCorrelationId,
        );
        this._setMigrationToken(mrGreenMigrationValues?.Token);
        DataStoreHelper.setNativeStoreItem('MrGreenMigrationValues', undefined);
    };

    private _setMigrationToken = (newTokenValue?: string) => {
        let migrationTokenData: IMigrationTokenItem = JSON.parse(
            this._localStorageItemService.get('mrGreenMigrationToken'),
        );

        if (newTokenValue != migrationTokenData?.Value) {
            this._token = newTokenValue;
            migrationTokenData = {
                Used: false,
                Value: this._token,
            };
            this._localStorageItemService.set(
                'mrGreenMigrationToken',
                JSON.stringify(migrationTokenData),
            );
        } else this._token = migrationTokenData?.Value;
    };

    private _getValidateBusinessCorrelationId = (businessCorrelationId: string) => {
        return businessCorrelationId && isValidUUID(businessCorrelationId)
            ? businessCorrelationId
            : uuidv4();
    };
}
